import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, Signal, signal, ViewContainerRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { FeatureFlagPipe, SortPipe } from '@core';
import { ScheduleControlComponent } from '@devices/components/schedule-control/schedule-control.component';
import { Store } from '@ngrx/store';
import { NotificationsService } from '@ui';
import { filter, take } from 'rxjs';

import { DeviceAttributeType, DeviceData, ModelActionPoint, RemoteAction, Workflow } from '../../../models';
import { DevicesSelectors } from '../../../ngrx-store';
import { DeviceRemoteActionService } from '../../../services';
import { ActionPointValueSelectorComponent } from '../../action-point-value-selector';
import { ConditionPickerComponent } from '../../condition-picker';
import { DatapointPickerComponent } from '../../datapoint-picker';
import { OperatorComponent } from '../../operator';
import { WorkflowsStore } from '../workflows/workflows.store';

interface Props {
  deviceId: string;
  workflow?: Workflow;
}

@Component({
  imports: [MatDialogModule, ReactiveFormsModule, MatProgressSpinnerModule, MatError, MatFormFieldModule, MatInputModule, MatSelectModule, SortPipe, MatIcon, ActionPointValueSelectorComponent, DatapointPickerComponent, ConditionPickerComponent, OperatorComponent, ScheduleControlComponent, FeatureFlagPipe],
  selector: 'avs-create-workflow-dialog',
  templateUrl: './create-workflow-dialog.component.html',
})
export class CreateWorkflowDialogComponent {
  protected data = inject<Props>(MAT_DIALOG_DATA);
  protected form: FormGroup;
  protected isLoading = signal(false);
  protected realtimeAttributes = computed(() =>
    this.device()?.additionalAttributes?.filter(
      attribute => attribute.attributeType === DeviceAttributeType.REALTIME,
    ) ?? [],
  );

  protected remoteActions = signal<RemoteAction[]>([]);
  protected selectedActionPoint = computed(() => {
    const device = this.device();
    const actionPointId = this.actionPointId();

    if (!device || !actionPointId) {
      return null;
    }

    const deviceAp = device.actionPoints?.find(ap => ap.id === actionPointId);

    if (deviceAp) {
      return deviceAp;
    }

    return this.remoteActions().find(ra => ra.id === actionPointId)?.deviceActionPoint ?? null;
  });
  protected readonly selectedActionPointRequired = computed<ModelActionPoint>(() => this.selectedActionPoint()!);
  protected readonly workflowStore = inject(WorkflowsStore);
  protected device: Signal<DeviceData | undefined>;
  protected formattedTimeout = computed(() => {
    const timeoutBetweenAttempts = this.timeoutBetweenAttempts();

    if (isNaN(timeoutBetweenAttempts) || timeoutBetweenAttempts < 0) {
      return null;
    }

    const h = Math.floor(timeoutBetweenAttempts / 3600);
    const m = Math.floor((timeoutBetweenAttempts % 3600) / 60);
    const s = timeoutBetweenAttempts % 60;

    const pad = (v: number) => v.toString().padStart(2, '0');

    return `${h}h:${pad(m)}m:${pad(s)}s`;
  });
  private actionPointId: Signal<string | undefined>;
  private dialogRef = inject(MatDialogRef);
  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationsService);
  private remoteActionService = inject(DeviceRemoteActionService);
  private timeoutBetweenAttempts = signal(0);
  private store = inject(Store);

  constructor() {
    this.remoteActionService.remoteActionList(this.data.deviceId).subscribe(list => {
      this.remoteActions.set(list);

      if (this.data.workflow?.remoteDeviceActionId) {
        const match = list.find(ra => ra.id === this.data.workflow!.remoteDeviceActionId);

        if (match) {
          this.form.patchValue({ actionPointId: match.id });
        }
      }
    });

    this.device = toSignal(
      this.store.select(DevicesSelectors.getDeviceById(this.data.deviceId)).pipe(
        filter(Boolean),
        take(1),
      ),
    );

    this.form = this.fb.group({
      actionPointId: [this.data.workflow?.remoteDeviceActionId ? null : this.data.workflow?.actionPointId, Validators.required],
      conditionValue: [this.data.workflow?.conditionValue, Validators.required],
      dataPoint: [this.data.workflow?.code, Validators.required],
      maxAttempts: [this.data.workflow?.maxAttempts || 1, [Validators.min(1), Validators.required]],
      name: [this.data.workflow?.name, Validators.required],
      operator: [this.data.workflow?.operator, Validators.required],
      schedule: [{
        config: this.data.workflow?.scheduleConfig,
        enabled: this.data.workflow?.scheduleEnabled || false,
      }],
      timeoutBetweenAttempts: [this.data.workflow?.timeoutBetweenAttempts || 60, [Validators.min(1), Validators.required]],
      value: [''],
    });

    this.actionPointId = toSignal(
      this.form.get('actionPointId')!.valueChanges,
      { initialValue: this.form.get('actionPointId')!.value },
    );


    this.timeoutBetweenAttempts = signal(
      this.form.get('timeoutBetweenAttempts')!.value,
    );

    this.form.get('timeoutBetweenAttempts')!.valueChanges.subscribe(val => {
      this.timeoutBetweenAttempts.set(val);
    });

  }


  protected async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    this.isLoading.set(true);

    const formValue = this.form.value;
    const selectedActionPointId = formValue.actionPointId;
    const isDeviceActionPoint = this.device()?.actionPoints?.some(ap => ap.id === selectedActionPointId) ?? false;
    const selectedRemoteAction = this.remoteActions().find(ra => ra.remoteDeviceActionPointId === selectedActionPointId);

    const isUpdate = !!this.data.workflow;
    const actionFields = isDeviceActionPoint
      ? {
        actionDeviceId: this.data.deviceId,
        actionPointId: selectedActionPointId,
        ...(isUpdate && { remoteDeviceActionId: null, remoteDeviceId: null }),
      }
      : {
        remoteDeviceActionId: selectedActionPointId,
        remoteDeviceId: selectedRemoteAction?.deviceId ?? null,
        ...(isUpdate && { actionDeviceId: null, actionPointId: null }),
      };

    const reqBody = {
      ...actionFields,
      code: formValue.dataPoint,
      conditionValue: formValue.conditionValue,
      deviceId: this.data.deviceId,
      maxAttempts: formValue.maxAttempts,
      name: formValue.name,
      operator: formValue.operator,
      scheduleConfig: formValue.schedule.config,
      scheduleEnabled: formValue.schedule.enabled,
      timeoutBetweenAttempts: formValue.timeoutBetweenAttempts,
      value: formValue.value,
    };

    try {
      const workflow = this.data.workflow
        ? await this.workflowStore.updateWorkflow(this.data.workflow.id, reqBody)
        : await this.workflowStore.createWorkflow(reqBody);

      if (workflow) {
        this.notificationService.showSuccessMessage(
          `Workflow [${workflow.name}] was successfully ${this.data.workflow ? 'updated' : 'created'}`,
        );
        this.dialogRef.close();
      }
    } catch (e) {
      this.notificationService.showErrorMessage((e as HttpErrorResponse).message);
    }
  }

  protected async deleteWorkflow() {
    try {
      const isDeleted = await this.workflowStore.deleteWorkflow(this.data.workflow!);

      if (isDeleted) {
        this.notificationService.showSuccessMessage(
          `Workflow [${this.data.workflow?.name}] was successfully removed`,
        );
        this.dialogRef.close();
      }
    } catch (e) {
      this.notificationService.showErrorMessage((e as HttpErrorResponse).message);
    }
  }
}

export function openCreateWorkflowDialog(dialog: MatDialog, data: Props, viewContainerRef: ViewContainerRef) {
  const config = new MatDialogConfig();

  config.disableClose = true;
  config.backdropClass = 'backdrop-modal-panel';
  config.data = data;
  config.viewContainerRef = viewContainerRef;
  const dialogRef = dialog.open(CreateWorkflowDialogComponent, config);

  return dialogRef.afterClosed();
}
